import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import axios from "axios";
import { identify } from "../capture";
import { useWhitelabel } from "../context/WhitelabelContext";
import { useAuth } from "../context/AuthContext";
import { hostname, trackAffiliate } from "../utils";

const MagicLinkVerify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [status, setStatus] = useState<string>("Verifying magic link...");
  const { whitelabelSettings } = useWhitelabel();
  const { signIn } = useAuth();

  useEffect(() => {
    const verifyMagicLink = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError("Invalid magic link token");
        setLoading(false);
        return;
      }

      try {
        // Verify the token and get custom token in one call
        const response = await axios.post(`${hostname}/auth/magic-link/verify`, {
          token: token
        });

        if (!response.data.success) {
          setError(response.data.error || "Invalid or expired magic link");
          setLoading(false);
          return;
        }

        const { email, customToken, uid } = response.data;
        setStatus("Magic link verified! Signing you in...");

        // Sign in with the custom token using AuthContext
        signIn(customToken, { email, uid });

        // Track analytics
        identify({ uid, email });
        trackAffiliate({ uid, email }, whitelabelSettings);

        setStatus("Successfully signed in! Checking your account...");

        // Check if user has companies to determine routing
        try {
          const [companiesResponse, usageResponse] = await Promise.all([
            axios.get(`${hostname}/companies`, {
              headers: { Authorization: `Bearer ${customToken}` },
            }),
            axios.get(`${hostname}/usage`, {
              headers: { Authorization: `Bearer ${customToken}` },
            }),
          ]);

          const hasCompanies = companiesResponse.data.companies && companiesResponse.data.companies.length > 0;
          const subscriptionType = usageResponse.data.usage?.subscriptionType || 'free';
          const isFree = subscriptionType.toLowerCase() === 'free' || subscriptionType === '';

          // Small delay before redirect to show success message
          setTimeout(() => {
            if (hasCompanies) {
              // User has companies, go to dashboard
              navigate('/dashboard/companies');
            } else if (whitelabelSettings?.disableFreeAccounts && !whitelabelSettings?.customSubscriptionEnabled && isFree) {
              // Only go to /subscribe if free accounts disabled AND user doesn't have a subscription
              navigate('/subscribe');
            } else {
              navigate('/onboarding');
            }
          }, 1500);
        } catch (checkError: any) {
          console.error("Error checking companies:", checkError);
          // On error, go to dashboard which will handle the error state
          setTimeout(() => {
            navigate('/dashboard/companies');
          }, 1500);
        }

      } catch (error: any) {
        console.error("Error verifying magic link:", error);
        let errorMessage = "Failed to verify magic link";

        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.code) {
          errorMessage = `Authentication error: ${error.message}`;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    verifyMagicLink();
  }, [searchParams, navigate, signIn, whitelabelSettings]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        padding: 3,
        textAlign: "center",
      }}
    >
      {loading ? (
        <>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {status}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we verify your magic link...
          </Typography>
        </>
      ) : error ? (
        <>
          <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The magic link may have expired or been used already.
            Please request a new magic link to sign in.
          </Typography>
        </>
      ) : (
        <>
          <Alert severity="success" sx={{ mb: 2, maxWidth: 400 }}>
            Magic link verified successfully!
          </Alert>
          <Typography variant="body2" color="text.secondary">
            You will be redirected to your dashboard shortly...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default MagicLinkVerify;
